/**
 * HM-386 cluster login PIN: Step 1 della LoginPage — grid 3-colonne operatori
 * attivi del tenant. Fetcha via useOperators (react-query, 5min stale).
 *
 * Stati:
 * - loading: skeleton testuale minimo (no spinner verboso, il container e' gia smooth)
 * - error: messaggio user-friendly con suggerimento /login-admin
 * - empty: messaggio "nessun operatore configurato" con cta admin
 * - success: griglia di OperatorCard
 */
import { useQuery } from '@tanstack/react-query';
import { type Operator, listOperators } from '../../api/auth.js';
import { OperatorCard } from './OperatorCard.js';

export function useOperators(hotelId?: string) {
  return useQuery({
    queryKey: ['auth', 'operators', hotelId ?? 'default'],
    queryFn: () => listOperators(hotelId),
    staleTime: 5 * 60 * 1000, // 5 min: gli operatori cambiano raramente
  });
}

export function OperatorsGrid({
  hotelId,
  onSelect,
  onBack,
}: {
  hotelId?: string;
  onSelect: (operator: Operator) => void;
  onBack?: () => void;
}) {
  const { data, isLoading, isError } = useOperators(hotelId);

  const Header = () => (
    <div className="login-step-header">
      {onBack && (
        <button
          type="button"
          className="change-user-btn"
          onClick={onBack}
          aria-label="Cambia hotel"
          data-testid="back-to-hotels"
        >
          ←
        </button>
      )}
      <div className="section-title">Seleziona operatore</div>
    </div>
  );

  if (isLoading) {
    return (
      <>
        <Header />
        <div style={{ textAlign: 'center', color: '#7A6957', padding: '24px 0' }}>
          Caricamento operatori…
        </div>
      </>
    );
  }
  if (isError) {
    return (
      <>
        <Header />
        <div
          role="alert"
          style={{ textAlign: 'center', color: '#A8453A', padding: '24px 0', fontSize: 13 }}
        >
          Impossibile caricare gli operatori. Verifica la connessione.
        </div>
      </>
    );
  }
  const operators = data?.operators ?? [];
  if (operators.length === 0) {
    return (
      <>
        <Header />
        <div style={{ textAlign: 'center', color: '#7A6957', padding: '24px 0', fontSize: 13 }}>
          Nessun operatore configurato. Accedi come <a href="/login-admin">admin</a>.
        </div>
      </>
    );
  }
  return (
    <>
      <Header />
      <div className="operators-grid">
        {operators.map((op) => (
          <OperatorCard key={op.id} operator={op} onSelect={onSelect} />
        ))}
      </div>
    </>
  );
}
